/**
 * Free Pump Program Calculator
 *
 * Vanilla JS port of the Gilair rental-vs-free-pump comparison tool.
 *
 * Pump rental rates are NOT hardcoded here - they are injected by Liquid from a
 * specific product's rental variants (see sections/page-free-pump-calculator.liquid)
 * as a day -> price (cents) map inside a <script data-fpc-config> tag.
 *
 * The "Rent these pumps" CTA links to that product page with ?rr_qty=<numPumps>,
 * which the product configurator reads to pre-populate the quantity.
 */

(function () {
  'use strict';

  const COMPETITOR_PUMP_COST = 0;

  /**
   * Format cents as USD. Falls back to a local formatter if the global
   * theme helper (js-global.js) has not loaded yet.
   */
  function money(dollars) {
    const cents = Math.round(dollars * 100);
    if (typeof window.formatMoney === 'function') {
      return window.formatMoney(cents);
    }
    return '$' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  class FreePumpCalculator {
    constructor(root) {
      this.root = root;

      const config = this.readConfig(root);
      if (!config) {
        console.warn('⚠️ FreePumpCalculator: no config found');
        return;
      }

      this.productUrl = config.productUrl || '#';
      this.maxDays = parseInt(config.maxDays, 10) || 14;
      this.verdictCopy = config.verdict || {};

      // Convert injected rate map (cents) -> dollars keyed by day number
      this.rates = {};
      Object.keys(config.rates || {}).forEach((day) => {
        this.rates[parseInt(day, 10)] = config.rates[day] / 100;
      });

      // Mutable state (seeded from section defaults)
      const d = config.defaults || {};
      this.state = {
        numPumps: this.clampToInput('numPumps', d.numPumps, 5),
        rentalDays: this.clampToInput('rentalDays', d.rentalDays, 5),
        totalSamples: this.clampToInput('totalSamples', d.totalSamples, 5),
        costPerSample: this.clampToInput('costPerSample', d.costPerSample, 35)
      };

      // Flat shipping averages (shown, not asked of the customer)
      this.raecoShip = Number(d.raecoShip);
      if (isNaN(this.raecoShip)) this.raecoShip = 25;
      this.competitorShip = Number(d.competitorShip);
      if (isNaN(this.competitorShip)) this.competitorShip = 750;

      this.tiers = this.buildTiers();

      this.cacheElements();
      this.syncInputsToState();
      this.bindEvents();
      this.renderRateChips();
      this.render();
    }

    readConfig(root) {
      const script = root.querySelector('[data-fpc-config]');
      if (!script) return null;
      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        console.error('❌ FreePumpCalculator: failed to parse config', error);
        return null;
      }
    }

    /**
     * Clamp a seed value to the matching range input's min/max so defaults
     * never start out-of-bounds.
     */
    clampToInput(key, value, fallback) {
      const input = this.root.querySelector(`[data-fpc-input="${key}"]`);
      let num = Number(value);
      if (isNaN(num)) num = fallback;
      if (input) {
        const min = Number(input.min);
        const max = Number(input.max);
        if (!isNaN(min)) num = Math.max(min, num);
        if (!isNaN(max)) num = Math.min(max, num);
      }
      return num;
    }

    cacheElements() {
      this.inputs = {};
      this.root.querySelectorAll('[data-fpc-input]').forEach((el) => {
        this.inputs[el.dataset.fpcInput] = el;
      });

      this.values = {};
      this.root.querySelectorAll('[data-fpc-value]').forEach((el) => {
        this.values[el.dataset.fpcValue] = el;
      });

      this.outputs = {};
      this.root.querySelectorAll('[data-fpc-out]').forEach((el) => {
        this.outputs[el.dataset.fpcOut] = el;
      });
    }

    syncInputsToState() {
      Object.keys(this.inputs).forEach((key) => {
        if (this.state[key] !== undefined) {
          this.inputs[key].value = this.state[key];
        }
      });
    }

    bindEvents() {
      Object.keys(this.inputs).forEach((key) => {
        const input = this.inputs[key];
        const handler = () => {
          this.state[key] = Number(input.value);
          this.render();
        };
        input.addEventListener('input', handler);
        input.addEventListener('change', handler);
      });
    }

    // ── RATE TIERS (derived from the live product rates) ──────────────────

    /**
     * Group consecutive days that share the same total price into tiers.
     * A multi-day tier with a constant price represents a "flat / capped" rate;
     * single-day tiers represent per-day pricing.
     */
    buildTiers() {
      const tiers = [];
      let current = null;

      for (let day = 1; day <= this.maxDays; day++) {
        const price = this.rateForDay(day);
        if (price === null) continue;

        if (current && current.price === price) {
          current.endDay = day;
        } else {
          current = { startDay: day, endDay: day, price: price };
          tiers.push(current);
        }
      }
      return tiers;
    }

    /**
     * Resolve the pump price for a given day. Falls back to the nearest lower
     * defined day, then to the highest defined rate.
     */
    rateForDay(day) {
      if (this.rates[day] !== undefined) return this.rates[day];

      let nearest = null;
      let highest = null;
      Object.keys(this.rates).forEach((k) => {
        const d = parseInt(k, 10);
        if (highest === null || d > highest) highest = d;
        if (d <= day && (nearest === null || d > nearest)) nearest = d;
      });

      if (nearest !== null) return this.rates[nearest];
      if (highest !== null) return this.rates[highest];
      return null;
    }

    calcPumpCost(days) {
      const d = Math.min(Math.max(days, 1), this.maxDays);
      const price = this.rateForDay(d);
      return price === null ? 0 : price;
    }

    getRateLabel(days) {
      const d = Math.min(Math.max(days, 1), this.maxDays);
      const tier = this.tiers.find((t) => d >= t.startDay && d <= t.endDay);
      if (!tier) return `${d}-day rate`;
      if (tier.startDay === tier.endDay) return `${d}-day rate`;
      return `${tier.startDay}\u2013${tier.endDay} day rate`;
    }

    renderRateChips() {
      const container = this.outputs.rateChips;
      if (!container) return;

      container.innerHTML = this.tiers
        .map((tier) => {
          const range =
            tier.startDay === tier.endDay
              ? `Day ${tier.startDay}`
              : `${tier.startDay}\u2013${tier.endDay} Days`;
          const isFlat = tier.endDay > tier.startDay;
          const val = isFlat
            ? `${money(tier.price)} flat`
            : `${money(tier.price)}`;
          return (
            '<div class="rr-fpc-rate-chip">' +
            `<div class="rr-fpc-rate-chip__label">${range}</div>` +
            `<div class="rr-fpc-rate-chip__val">${val}</div>` +
            '</div>'
          );
        })
        .join('');
    }

    // ── CALCULATIONS (port of the original React logic) ───────────────────

    compute() {
      const s = this.state;
      const totalSamples = Math.max(0, s.totalSamples);

      // RAECO Rents: rent the pump + flat-average shipping
      const pumpCostPerUnit = this.calcPumpCost(s.rentalDays);
      const raecoPumpTotal = pumpCostPerUnit * s.numPumps;
      const raecoShipping = this.raecoShip;
      const raecoTotal = raecoPumpTotal + raecoShipping;

      // Provider G: free pumps, but you pay their lab analysis + round-trip shipping
      const competitorLab = totalSamples * s.costPerSample;
      const competitorShipping = this.competitorShip;
      const competitorTotal = competitorLab + competitorShipping;

      const savings = competitorTotal - raecoTotal;

      return {
        totalSamples,
        pumpCostPerUnit,
        raecoPumpTotal,
        raecoShipping,
        raecoTotal,
        competitorLab,
        competitorShipping,
        competitorTotal,
        savings,
        raecoWins: savings > 0
      };
    }

    // ── RENDER ────────────────────────────────────────────────────────────

    setText(key, text) {
      if (this.outputs[key]) this.outputs[key].textContent = text;
    }

    render() {
      const s = this.state;
      const c = this.compute();
      const pumpsWord = s.numPumps !== 1 ? 'pumps' : 'pump';
      const samplesWord = c.totalSamples !== 1 ? 'samples' : 'sample';

      // RAECO Rents breakdown
      this.setText('raecoPumpRateSub', `${this.getRateLabel(s.rentalDays)} \u00d7 ${s.numPumps} ${pumpsWord}`);
      this.setText('raecoPumpRate', money(c.pumpCostPerUnit));
      this.setText('raecoPumpTotal', money(c.raecoPumpTotal));
      this.setText('raecoShippingSub', 'flat average, round trip');
      this.setText('raecoShipping', money(c.raecoShipping));
      this.setText('raecoTotal', money(c.raecoTotal));

      // Provider G breakdown
      this.setText('compLabSub', `${c.totalSamples} ${samplesWord} \u00d7 ${money(s.costPerSample)}`);
      this.setText('compLabTotal', money(c.competitorLab));
      this.setText('compShippingSub', 'average round-trip shipping');
      this.setText('compShipping', money(c.competitorShipping));
      this.setText('compTotal', money(c.competitorTotal));

      // Verdict
      this.renderVerdict(c);

      // CTA -> product page with quantity prefilled
      this.updateCta();
    }

    renderVerdict(c) {
      const card = this.outputs.verdictCard;
      if (card) {
        card.classList.toggle('rr-fpc-verdict--win', c.raecoWins);
        card.classList.toggle('rr-fpc-verdict--lose', !c.raecoWins);
      }

      this.setText(
        'verdictEyebrow',
        c.raecoWins ? 'Your numbers are working for you' : 'The free pumps are doing their job'
      );

      this.setText(
        'verdictHeadline',
        c.raecoWins
          ? `You're saving ${money(c.savings)} compared to the free pump program`
          : `The free pump program saves ${money(Math.abs(c.savings))} at these volumes`
      );

      this.setText('verdictBody', c.raecoWins ? this.verdictCopy.winText || '' : this.verdictCopy.loseText || '');

      this.setText('verdictRaecoTotal', money(c.raecoTotal));
      this.setText('verdictCompTotal', money(c.competitorTotal));
      this.setText('verdictDiffLabel', c.raecoWins ? 'You Save' : 'Difference');
      this.setText('verdictDiff', money(Math.abs(c.savings)));
      this.setText('verdictDiffSub', c.raecoWins ? 'with RAECO Rents' : 'in favor of free pumps');
    }

    updateCta() {
      const cta = this.outputs.rentCta;
      if (!cta) return;

      let url = this.productUrl;
      try {
        const parsed = new URL(this.productUrl, window.location.origin);
        parsed.searchParams.set('rr_qty', String(this.state.numPumps));
        url = parsed.pathname + parsed.search;
      } catch (error) {
        const sep = this.productUrl.indexOf('?') === -1 ? '?' : '&';
        url = `${this.productUrl}${sep}rr_qty=${this.state.numPumps}`;
      }

      cta.setAttribute('href', url);

      const pumpsWord = this.state.numPumps !== 1 ? 'pumps' : 'pump';
      this.setText('rentCtaLabel', `Rent ${this.state.numPumps} ${pumpsWord}`);
    }
  }

  function init() {
    document.querySelectorAll('[data-fpc]').forEach((root) => {
      new FreePumpCalculator(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
